import { CategoryDisplayName } from '@oxappsec/ox-consolidated-categories';
import { OneOfValues, RepoTypes } from 'common-types';
import { IconType } from 'common-types';
import {
  AppOwner,
  AppOwnerRole,
  Nullable,
  Order,
  SbomType,
  SeverityAlert,
  SeverityType,
} from 'common-types';
import { FilterElements } from '../common/filters-utils/filters-utils';
import { InventoryFilterType } from '../dashboard-inventory/inventory-types';
import { ApplicationFlow } from '../elements/AppFlow/types/app-flow-types';

export interface BaseApplicationInfo {
  new: boolean;
  risk: number;
  type: RepoTypes;
  size: number;
  appId: string;
  issues: number;
  scanId: string;
  branch: string;
  creator: string;
  appName: string;
  version: string;
  updated: boolean;
  fakeApp: boolean;
  relevant: boolean;
  scannedAt: string;
  userCount: number;
  pushCount: number;
  pullCount: number;
  tagsCount: number;
  createdAt: string;
  yamlsCount: number;
  filesCount: number;
  forksCount: number;
  categories: Category[];
  codeChanges: number;
  commitCount: number;
  deployedProd: boolean;
  hasDownloads: boolean;
  branchesCount: number;
  watchersCount: number;
  violationCount: number;
  lastCodeChange: string;
  securityPosture: number;
  overridePriority: number;
  publicVisibility: boolean;
  businessPriority: number;
  irrelevantReasons: string[];
  overrideRelevance: string;
  daysSinceRepoCreation: number;
  daysSinceLastCodeChange: number;
  sbomCount: number;
  languages: {
    language: string;
    languagePercentage: number;
  }[];
  applicationFlows: ApplicationFlow;
  appOwners?: AppOwner[];
  owners: GroupedOwners;
  moreOwners: Owner[];
  shortName: string;
  link: string;
  scoreHistory: {
    date: number;
    score: number;
  }[];
  repoIcon: Nullable<IconType>;
  exclusionComment?: string;
  pipeline: Pipeline;
  isSbomPresent: boolean;
  pipelineScans: number;
  isMonoRepoChild: boolean;
  monoRepoParent: string;
}

export type GroupedOwners = Partial<
  Record<OneOfValues<typeof AppOwnerRole>, Owner[]>
>;

export interface Owner {
  name: string;
  email: string;
}

export interface PolicyList {
  policyName: string;
  alertCount: number;
  appCount: number;
  oxId: string | number;
}

export interface Category {
  name: string;
  policies: PolicyList[];
  policyCount: number;
  policyTotal: number;
}

export interface ApplicationInfo extends BaseApplicationInfo {
  flows: ApplicationFlow;
  lastCodeChangeDate?: string;
  repoIcon: Nullable<IconType>;
}

export interface BaseApplicationRow {
  appId: string;
  appName: string;
  type: RepoTypes;
  fakeApp: boolean;
  categories: AppCategory[];
  risk: number;
  businessPriority: number;
  securityPosture: number;
  appOwners?: AppOwner[];
  issuesBySeverity: SeverityAlert;
  owners: Partial<Record<OneOfValues<typeof AppOwnerRole>, Owner[]>>;
  scoreHistory: {
    date: number;
    score: number;
  }[];
  userCount: number;
  codeChanges: number;
  lastCodeChange?: string;
  exclusionComment: string;
  pipeline: Pipeline;
  isSbomPresent: boolean;
  sbomCount: number;
  isMonoRepoChild: boolean;
  monoRepoParent: string;
}

export interface AppCategory {
  categoryName: CategoryDisplayName;
  categoryId: string;
  catId: number;
  severities: {
    info: number;
    low: number;
    medium: number;
    high: number;
    critical: number;
    appox: number;
  };
  severityScore: SeverityType;
  score: number;
  total: number;
  isNa: boolean;
  reason: string[];
}

export type ApplicationsStoreCategories = Pick<
  AppCategory,
  'catId' | 'categoryName'
>[];

export interface ApplicationRow extends BaseApplicationRow {
  categoriesMap: { [key: string]: AppCategory };
  shortName: string;
  repoIcon: Nullable<IconType>;
  lastCodeChangeDate: string;
  pipeline: Pipeline;
}

export enum AppSystemsTypes {
  cicd = 'cicd',
  cloudDeployments = 'cloudDeployments',
}
export interface GetApplicationsInput {
  isAppIdOnly?: boolean;
  limit?: number;
  offset: number;
  applicationFilters?: InventoryFilterType[];
  owners?: string[];
  orderBy?: {
    field?: string;
    direction: Order;
    category?: string;
  };
  filters?: {};
  systemFilter?: {
    name: AppSystemsTypes;
    type: string;
  };
  search?: string;
  scanId?: string;
}

export interface FilterApplicationsType {
  categories: FilterElements[];
  repoTypes: FilterElements[];
  orchestrators: FilterElements[];
  artifacts: FilterElements[];
  cicd: FilterElements[];
  cloudDeployments: FilterElements[];
  kubernetes: FilterElements[];
  sca: FilterElements[];
  sast: FilterElements[];
  secretSearch: FilterElements[];
  iac: FilterElements[];
  securityToolSource: FilterElements[];
}

export interface FetchApplicationsResponse {
  getApplicationsFilters: FilterApplicationsType;
}

export interface Pipeline {
  jobId: string;
  jobTriggeredAt: number;
  scanResult: PipelineScanResult;
  issuesCount: number;
  jobTriggeredBy: string;
  jobUrl: string;
}

export enum PipelineScanResult {
  none = 'none',
  monitor = 'monitor',
  block = 'block',
}

export interface GetOrgSbomInut {
  downloadType: DownloadType;
  appId?: string[];
  type?: SbomType;
  filters?: {};
  search?: string;
}

export enum DownloadType {
  CSV = 'CSV',
  JSON = 'JSON',
}

export interface ExportSBOMToCSVResponse {
  appId?: string;
  fileName?: string;
  sbomCSV?: string;
}
